import { integrationsRequest } from '../lib/integrations';
import { localAIEnabled, modelProvider, analyzeAutopilotBatch, analyzeMeetingWindow } from './ollama';
import { CalendarService } from './calendarServices';
import { currentUser } from './database';
import { runJob } from '../lib/backgroundJobs';

export const getAutopilot = () => integrationsRequest('/api/autopilot');
export const saveAutopilot = settings => integrationsRequest('/api/autopilot/settings', { body: settings });
export const refreshAutopilot = force => integrationsRequest('/api/autopilot/run', { body: { force } });
const ownerCheck = async (uid, signal) => {
  if (signal?.aborted || (await currentUser()).id !== uid) throw new Error('The SPCC session changed.');
};

// Calls analyzed with a local model run here, one transcript window per session tick.
async function analyzeLocalMeeting(uid, model, signal, report) {
  if (!localAIEnabled || !model || modelProvider(model) !== 'ollama') return;
  let task;
  try { ({ task } = await integrationsRequest('/api/meetings/analysis/local-task', { body: {}, signal })); }
  catch (error) { if (error.status !== 404) report(error.message); return; }
  if (!task) return;
  const lease = { meeting_id: task.meeting_id, lease_id: task.lease_id };
  report(`Local Ollama is analyzing a call (part ${task.index + 1} of ${task.total})…`);
  try {
    await runJob('Analyzing a call with local AI', async () => {
      const { answer } = await analyzeMeetingWindow({ model: task.model, question: task.question, context: task.context, signal });
      await ownerCheck(uid, signal);
      await integrationsRequest('/api/meetings/analysis/local-result', { body: { ...lease, answer }, signal });
    });
    window.dispatchEvent(new Event('spcc:data-changed'));
  } catch (error) {
    if (!signal?.aborted && (await currentUser()).id === uid) await integrationsRequest('/api/meetings/analysis/local-failed', { body: lease }).catch(() => {});
    report(error.message);
  }
}

export async function runAutopilotSession(uid, signal, report = () => {}) {
  let state = await getAutopilot();
  await ownerCheck(uid, signal);
  // Call analysis does not depend on Autopilot being enabled, only on its chosen model.
  await analyzeLocalMeeting(uid, state.settings.model, signal, report);
  await ownerCheck(uid, signal);
  if (!state.settings.enabled) return state;
  if (state.settings.calendars && (!state.last_calendar_at || Date.now() - state.last_calendar_at >= state.settings.intervalMinutes * 60000)) {
    try {
      const service = new CalendarService(uid);
      await service.outlookService.initialize();
      if (!service.outlookService.isAuthenticated) throw new Error('Connect Outlook on this device to refresh its calendar.');
      const entries = await runJob('Refreshing Outlook calendar', async () => {
        const start = new Date(); start.setDate(start.getDate() - 7);
        const end = new Date(); end.setDate(end.getDate() + 7);
        const events = await service.importEvents(start, end, ['outlook']);
        await ownerCheck(uid, signal);
        for (let offset = 0; offset < Math.max(1, events.length); offset += 200) {
          await ownerCheck(uid, signal);
          await integrationsRequest('/api/autopilot/calendar', { body: { entries: events.slice(offset, offset + 200) }, signal });
        }
        return events;
      });
      report(`Outlook refreshed: ${entries.length} events checked.`);
    } catch (error) { report(error.message); }
  }
  await ownerCheck(uid, signal);
  if (!state.next_run_at || state.next_run_at <= Date.now()) state = await runJob('Autopilot is updating insights', () => refreshAutopilot(false));
  if (localAIEnabled && modelProvider(state.settings.model) === 'ollama') {
    await ownerCheck(uid, signal);
    const { task } = await integrationsRequest('/api/autopilot/local-task', { body: {}, signal });
    if (task) {
      report('Local Ollama is analyzing new evidence…');
      try {
        await runJob('Analyzing new evidence with local AI', async () => {
          const { answer } = await analyzeAutopilotBatch({ model: task.model, context: task.context, signal });
          await ownerCheck(uid, signal);
          await integrationsRequest('/api/autopilot/local-result', { body: { task_id: task.id, answer }, signal });
        });
        report('Local analysis saved.');
      } catch (error) {
        if (!signal?.aborted && (await currentUser()).id === uid) await integrationsRequest('/api/autopilot/local-failed', { body: { task_id: task.id } });
        throw error;
      }
    }
  }
  return getAutopilot();
}
